import {
  CJPromoShelf,
  galateaNotificationPriorityLiteral,
  galateaNotificationTypeLiteral,
  OfferPopupConfig,
} from '@inkitt/types-galatea';
import { galateaTVSupportedLanguageCodes } from '@inkitt/types-galateatv';
import { sql } from 'drizzle-orm';
import {
  bigserial,
  boolean,
  check,
  date,
  index,
  integer,
  jsonb,
  pgEnum,
  pgSchema,
  real,
  serial,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { SocialGroupForPointsLiterals } from '../../types/SocialGroupForPoints';
import type { UnlimitedSubscriptionHistoryState } from '../../types/subscriptionHistoryTypes';
import { TransferResult } from '../../types/UserTransfer';

export const galateaPgSchema = pgSchema('galatea');

export const notificationType = pgEnum('notification_type', galateaNotificationTypeLiteral);
export const notificationPriority = pgEnum('notification_priority', galateaNotificationPriorityLiteral);
export const reactionType = pgEnum('reaction_type', ['like']);
export const BookStoreRegions = pgEnum('book_store_region', ['US', 'EU']);

type NotificationMetadata = {
  url?: string;
  seriesId?: string;
  episodeId?: number;
  commentId?: number;
  reactionType?: string;
  screen?: string;
  postId?: number;
  authorId?: string;
  page?: string;
  list_id?: string | number;
  readingListId?: string | number;
};

export const userNotifications = galateaPgSchema.table(
  'user_notifications',
  {
    id: serial('id').primaryKey(),
    key: text('key').notNull(),
    userId: text('user_id').notNull(),
    title: text('title').notNull(),
    body: text('body').notNull(),
    isDismissed: text('is_dismissed').notNull().default('false'),
    type: notificationType('type').notNull().default('other'),
    priority: notificationPriority('priority').notNull().default('normal'),
    metadata: jsonb('metadata').notNull().$type<NotificationMetadata>().default({}),
    showAfter: timestamp('show_after', { withTimezone: true }),
    showUntil: timestamp('show_until', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    uniqueUserNotification: unique().on(table.userId, table.key),
  }),
);

export const userNotificationMetadata = galateaPgSchema.table('user_notification_metadata', {
  userId: text('user_id').primaryKey(),
  lastSeenAt: timestamp('last_seen_at', { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
});

export const userStoriesComments = galateaPgSchema.table(
  'comments',
  {
    id: serial('id').primaryKey(),
    body: varchar('body'),
    episode: integer('episode'),
    episode_version: integer('episode_version'),
    username: varchar('username'),
    user_id: varchar('user_id'),
    firebase_id: varchar('firebase_id'),
    path: varchar('path'),
    story: varchar('story'),
    created_at: timestamp('created_at', { withTimezone: false }).notNull().defaultNow(),
    updated_at: timestamp('updated_at', { withTimezone: false }).notNull().defaultNow(),
    body_length: integer('body_length'),
    likes_count: integer('likes_count'),
    replies_count: integer('replies_count'),
    parent_comment_id: integer('parent_comment_id'),
    user_avatar: text('user_avatar'),
    user_subscription_status: boolean('user_subscription_status'),
    is_hidden: boolean('is_hidden'), // hidden comments by operation team
    is_blocked: boolean('is_blocked'), // this blocked comments by AI model
    files: jsonb('files').$type<Array<string>>(),
    sentiment_score: real('sentiment_score'),
  },
  (table) => {
    return {
      adminFullSearchIndex: index('comments_admin_full_search_index').on(table.user_id, table.username, table.story),
      adminSearchIndex: index('comments_admin_search_index').on(table.username, table.story),
      indexCommentsManualOnStoryEpisodeCreatedAt: index('index_comments_manual_on_story_episode_created_at').on(
        table.story,
        table.episode,
        table.created_at,
      ),
      indexCommentsOnCreatedAt: index('comments_index_comments_on_created_at').on(table.created_at),
      indexCommentsOnEpisode: index('comments_index_comments_on_episode').on(table.episode),
      indexCommentsOnStory: index('comments_index_comments_on_story').on(table.story),
      indexCommentsUserIdCreatedAt: index('index_comments_user_id_created_at').on(table.user_id, table.created_at),
    };
  },
);

export const userStoriesCommentsReports = galateaPgSchema.table(
  'comments_reports',
  {
    id: serial('id').primaryKey(),
    reporter_user_id: varchar('reporter_user_id').notNull(),
    story: varchar('story').notNull(),
    episode: integer('episode').notNull(),
    reported_comment_id: integer('reported_comment_id').notNull(),
    reason: varchar('reason').notNull().default('inappropriate'),
    is_processed: boolean('is_processed').notNull().default(false),
    status: varchar('status').notNull().default('pending'),
    created_at: timestamp('created_at', { withTimezone: false }).notNull().defaultNow(),
  },
  (table) => ({
    adminFullSearchIndex: index('comments_reports_admin_full_search_index').on(table.reporter_user_id, table.story),
    adminSearchIndex: index('comments_reports_admin_search_index').on(table.story),
    indexCommentsOnCreatedAt: index('comments_reports_index_comments_on_created_at').on(table.created_at),
    indexCommentsOnEpisode: index('comments_reports_index_comments_on_episode').on(table.episode),
    indexCommentsOnStory: index('comments_reports_index_comments_on_story').on(table.story),
  }),
);

export const userStoriesCommentsReaction = galateaPgSchema.table(
  'comments_reaction',
  {
    id: serial('id').primaryKey(),
    commentId: integer('comment_id').notNull(),
    userId: text('user_id').notNull(),
    reactionType: reactionType('type').notNull().default('like'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    uniqueUserCommentsReaction: unique().on(table.commentId, table.userId),
    commentUserIdx: index('comment_user_idx').on(table.commentId, table.userId),
    commentIdx: index('comment_idx').on(table.commentId),
  }),
);

export const userBlocks = galateaPgSchema.table(
  'user_blocks',
  {
    id: uuid('id').primaryKey(),
    blockerUserId: text('blocker_user_id').notNull(),
    blockedUserId: text('blocked_user_id').notNull(),
    contextCommentId: integer('context_comment_id').references(() => userStoriesComments.id),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
  },
  (table) => ({
    blockerActiveIdx: index('user_blocks_blocker_active_idx')
      .on(table.blockerUserId)
      .where(sql`${table.deletedAt} IS NULL`),
    blockerBlockedActiveUniqueIdx: uniqueIndex('user_blocks_blocker_blocked_active_uq')
      .on(table.blockerUserId, table.blockedUserId)
      .where(sql`${table.deletedAt} IS NULL`),
    noSelfBlock: check('user_blocks_no_self_block_chk', sql`${table.blockerUserId} <> ${table.blockedUserId}`),
  }),
);

export const storySocialProofStatistics = galateaPgSchema.table(
  'story_social_proof_statistics',
  {
    seriesId: text('series_id').primaryKey(),
    chaptersRead: integer('chapters_read').notNull().default(0),
    comments: integer('comments').notNull().default(0),
    bookmarksSaved: integer('bookmarks_saved').notNull().default(0),
    shares: integer('shares').notNull().default(0),
    storyStarts: integer('story_starts').notNull().default(0),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    uniqueStorySocialProofStatistics: unique().on(table.seriesId),
  }),
);

export const userData = galateaPgSchema.table(
  'user_data',
  {
    id: serial('id').primaryKey(),
    userId: text('user_id').notNull().unique(),
    username: text('username').unique(),
    avatarUrlPath: text('avatar_url_path'),
    marketingSegmentId: text('marketing_segment_id'),
    shareToken: text('share_token').unique(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    firstInstalledOnDateSyncedProjects: text('first_installed_on_date_synced_projects').array().notNull().default([]),
    profileUpdatePromptCount: integer('profile_update_prompt_count'),
    isUsernameAutoGenerated: boolean('is_username_auto_generated'),
    isUserHasActiveSubscription: boolean('is_user_has_active_subscription'),
    dateOfBirth: date('date_of_birth'),
    /**
     * Written synchronously by RevenueCat on unlimited purchases, renewals and transfers, and by Stripe
     * on successful unlimited subscription invoices. RevenueCat clears it on expiration. Mirrors
     * `galateatv.user.lastUnlimitedEntitlementPurchaseAt`: the entitlements cache is busted on a
     * ~1-minute delay, so this is how a read path can tell that "this user just paid" before the cache
     * catches up (see `wasRecentUnlimitedPurchase`).
     */
    lastUnlimitedEntitlementPurchaseAt: timestamp('last_unlimited_entitlement_purchase_at', { withTimezone: true }),
    /**
     * Set once, on a user's FIRST ever unlimited purchase, and never cleared — renewals, transfers and
     * re-subscriptions leave it untouched. Distinguishes first-time subscribers from renewers so
     * subscriber D0 (welcome) content only ever goes out once (DEV-6062). A null value means "not known
     * to be a first-ever purchase", which fails closed: no D0 content.
     */
    firstUnlimitedEntitlementPurchaseAt: timestamp('first_unlimited_entitlement_purchase_at', { withTimezone: true }),
    /**
     * CRM-safe subscription history. Migrated rows start `unknown` and therefore fail closed; only a
     * row created by the account-registration path starts `never_subscribed` and may become
     * `first_subscription`. A renewal, transfer, or later INITIAL_PURCHASE makes it returning.
     */
    unlimitedSubscriptionHistoryState: text('unlimited_subscription_history_state')
      .$type<UnlimitedSubscriptionHistoryState>()
      .notNull()
      .default('unknown'),
  },
  (table) => ({
    uniqueUserProfile: index('unique_user_profile').on(table.userId, table.username),
  }),
);

export const storyEpisodeCommentsCount = galateaPgSchema.table(
  'story_episode_comments_count',
  {
    id: serial('id').primaryKey(),
    story: text('story'),
    episode: integer('episode'),
    commentsCount: integer('comments_count'),
  },
  (table) => ({
    storyEpisodeIndex: index('story_episode_index').on(table.story, table.episode),
  }),
);

// These two tables are for targeting users based on content engagement. The reason we have both
// is that these are getting populated for all users at the same time from CSV data. This is
// roughly 850k+ rows (as of 2025-02-07) and the most efficient way to do this is a stream.
// However, streaming data into a table with an index is pretty inefficient and slow, but
// the `COPY` command is very well optimized. Because of this, we first stream the data
// into the unindexed staging table below and then copy it over to the primary indexed table.
export const userContentEngagement = galateaPgSchema.table('user_content_engagement', {
  userId: text('user_id').primaryKey(),
  stories: integer('stories').notNull(),
  chapters: integer('chapters').notNull(),
});

export const userContentEngagementStaging = galateaPgSchema.table('user_content_engagement_staging', {
  userId: text('user_id').notNull(),
  stories: integer('stories').notNull(),
  chapters: integer('chapters').notNull(),
});

export const familyPlanManager = galateaPgSchema.table(
  'family_plan_manager',
  {
    id: serial('id').primaryKey(),
    userId: text('user_id').notNull().unique(),
    familyJoinCode: text('family_join_code').notNull().unique(),
    familyPlanCountry: text('family_plan_country').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    familyPlanSubscriptionIdx: index('family_plan_subscription_idx').on(table.userId),
    familyJoinCodeIdx: index('family_join_code_idx').on(table.familyJoinCode),
  }),
);

export const familyPlanMember = galateaPgSchema.table(
  'family_plan_member',
  {
    id: serial('id').primaryKey(),
    userId: text('user_id').notNull().unique(),
    // TODO: this should be renamed to familyPlanManagerId
    familyPlanSubscriptionId: integer('family_plan_subscription_id')
      .notNull()
      .references(() => familyPlanManager.id, {
        onDelete: 'cascade',
      }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    userIdIndex: index('user_id_index').on(table.userId),
    familyPlanManagerIdx: index('family_plan_manager_idx').on(table.familyPlanSubscriptionId),
  }),
);

export const bookPromoCode = galateaPgSchema.table(
  'book_promo_code',
  {
    id: serial('id').primaryKey(),
    code: text('code').notNull().unique(),
    userId: text('user_id'),
    seriesId: text('series_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    userIdIndex: index('userid_idx').on(table.userId),
    codeIndex: index('code_index').on(table.code),
  }),
);

export const shopifyOrderRegionMapping = galateaPgSchema.table('shopify_order_region_mapping', {
  orderId: text('order_id').primaryKey(),
  // Order shopify store region. Orders from Galatea app are stored in US store, orders from Galatea web are stored in both US and EU stores.
  shopifyStoreRegion: BookStoreRegions('shopify_store_region').notNull(),
  // Order delivery region.
  ingramRegion: BookStoreRegions('ingram_region').notNull(),
});

export const topOnePercentCustomers = galateaPgSchema.table('top_one_percent_customers', {
  userId: text('user_id').primaryKey(),
  chapters: integer('chapters').notNull(),
  rank: integer('rank').notNull(),
});

export const linkType = pgEnum('link_type', ['199_offer']);

export const oneTimeLink = galateaPgSchema.table(
  'one_time_link',
  {
    id: serial('id').primaryKey(),
    userId: text('user_id').notNull(),
    type: linkType('type').notNull().default('199_offer'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    userIdTypeUnique: unique().on(table.userId, table.type),
  }),
);

export const userTransferHistory = galateaPgSchema.table('user_transfer_history', {
  id: bigserial('id', { mode: 'number' }).primaryKey(),
  sourceUserId: text('source_user_id').notNull(),
  targetUserId: text('target_user_id').notNull(),
  trigger: text('trigger').notNull(),
  result: jsonb('result').$type<TransferResult>(),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
});

export const SocialGroupsForPoints = pgEnum('social_groups_for_points', SocialGroupForPointsLiterals);

export const socialGroupClaimHistory = galateaPgSchema.table(
  'social_group_points_claim_history',
  {
    id: bigserial('id', { mode: 'number' }).primaryKey(),
    userId: text('user_id').notNull(),
    deviceId: text('device_id').notNull(),
    socialGroup: SocialGroupsForPoints('social_group').notNull(),
    points: integer('points').notNull(),
    notificationShownAt: timestamp('notification_shown_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => {
    return {
      deviceIdIdx: index('device_id_idx').on(table.deviceId),
      uniquePerDeviceId: unique().on(table.deviceId, table.socialGroup),
    };
  },
);

export const revcatUserSubscriptionDumps = galateaPgSchema.table('revcat_user_subscription_dumps', {
  userId: text('user_id').notNull().primaryKey(),
  country: text('country'),
  presentedOffering: text('presented_offering'),
  productIdentifier: text('product_identifier'),
  endTime: timestamp('end_time', { withTimezone: true }),
  unsubscribeDetectedAt: timestamp('unsubscribe_detected_at', { withTimezone: true }),
  platform: text('platform'),
});

export const galateaAuthorIterableListIds = galateaPgSchema.table('galatea_author_iterable_list_ids', {
  authorId: text('author_id').notNull(),
  listId: integer('list_id'),
});

export const galateaUserFollowingAuthor = galateaPgSchema.table('galatea_user_following_author', {
  authorId: text('author_id').notNull(),
  followerUserId: text('user_id').notNull(),
});

export const gtvSeriesLanguageEnum = pgEnum('gtv_series_language_enum', galateaTVSupportedLanguageCodes);

export const candyJarPromoShelf = galateaPgSchema.table(
  'candy_jar_promo_shelf',
  {
    id: serial('id').primaryKey(),
    language: gtvSeriesLanguageEnum('language').notNull(),
    /** @deprecated */
    variant: text('variant').notNull().default('default'),
    shelfData: jsonb('shelf_data').notNull().$type<CJPromoShelf>(),
  },
  (table) => ({
    uniqueVariant: unique().on(table.variant, table.language),
  }),
);

export const dynamicPaywallPopupConfigs = galateaPgSchema.table('dynamic_paywall_popup_configs', {
  id: serial('id').primaryKey(),
  name: text('name').notNull(),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  config: jsonb('config').notNull().$type<OfferPopupConfig>(),
});

export const adSkipsBalance = galateaPgSchema.table('ad_skips_balance', {
  userId: text('user_id').primaryKey(),
  skips: integer('skips').notNull().default(0),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
});

export const adSkipsTransactionType = pgEnum('ad_skips_transaction_type', ['earn', 'spend']);

export const adSkipsTransaction = galateaPgSchema.table(
  'ad_skips_transaction',
  {
    id: bigserial('id', { mode: 'number' }).primaryKey(),
    userId: text('user_id').notNull(),
    type: adSkipsTransactionType('type').notNull(),
    /** Positive for `earn` (+N), negative for `spend` (-1). */
    amount: integer('amount').notNull(),
    /** Balance after this transaction was applied, for auditability. */
    balanceAfter: integer('balance_after').notNull(),
    /**
     * Idempotency key. Earn: a stable id per rewarded-ad reward. Spend: a stable
     * id per countdown unlock. The unique(userId, transactionId, type) constraint
     * makes retries safe and prevents double-credit / double-spend. `type` is part
     * of the key so an earn and a spend that happen to share a transactionId do not
     * collide (each type dedupes independently).
     */
    transactionId: text('transaction_id').notNull(),
    /** Surface that triggered the transaction, e.g. 'pread_screen' | 'more_screen' | 'countdown'. */
    source: text('source'),
    seriesId: text('series_id'),
    episodeId: text('episode_id'),
    metadata: jsonb('metadata').$type<{ adProvider?: string; placement?: string }>(),
    createdAt: timestamp('timestamp', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    uniqueTransaction: unique('ad_skips_tx_user_transaction_unique').on(table.userId, table.transactionId, table.type),
    userIdIdx: index('ad_skips_tx_user_id_idx').on(table.userId),
    createdAtIdx: index('ad_skips_tx_created_at_idx').on(table.createdAt),
  }),
);

export const galateaSchema = {
  userNotifications,
  userNotificationMetadata,
  userStoriesComments,
  userStoriesCommentsReports,
  storySocialProofStatistics,
  userStoriesCommentsReaction,
  userBlocks,
  userData,
  storyEpisodeCommentsCount,
  userContentEngagement,
  userContentEngagementStaging,
  familyPlanManager,
  familyPlanMember,
  bookPromoCode,
  shopifyOrderRegionMapping,
  topOnePercentCustomers,
  oneTimeLink,
  userTransferHistory,
  socialGroupClaimHistory,
  revcatUserSubscriptionDumps,
  candyJarPromoShelf,
  galateaAuthorIterableListIds,
  galateaUserFollowingAuthor,
  dynamicPaywallPopupConfigs,
  adSkipsBalance,
  adSkipsTransaction,
};
